import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../ui/table";
import { Skeleton } from "../../../ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../ui/alert-dialog";
import { Plus, MailPlus, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { fetchBlastsWithStats, sendBlast, deleteBlast, type BlastRow } from "../../../../lib/blastsEngageV1";

const statusClass = (s: BlastRow["status"]) => {
  switch (s) {
    case "sent":
      return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200";
    case "sending":
      return "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200";
    case "scheduled":
      return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200";
    case "draft":
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

const formatPct = (n: number | null) => (n === null ? "—" : `${n.toFixed(1)}%`);
const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const Outreach = () => {
  const { merchant } = useMerchant();
  const [rows, setRows] = useState<BlastRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<BlastRow | null>(null);

  const reload = async () => {
    if (!merchant) return;
    try {
      const r = await fetchBlastsWithStats(merchant.id);
      setRows(r);
    } catch (e) {
      console.error("Failed to load blasts", e);
    }
  };

  useEffect(() => {
    if (!merchant) return;
    let cancelled = false;
    setLoading(true);
    fetchBlastsWithStats(merchant.id)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((e) => console.error("Failed to load blasts", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [merchant]);

  const handleSend = async (r: BlastRow) => {
    setSendingId(r.id);
    try {
      await sendBlast(r.id);
      toast.success("Campaign send started");
      await reload();
    } catch (e: any) {
      toast.error(e?.message || "Failed to send campaign");
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setDeletingId(id);
    try {
      await deleteBlast(id);
      setRows((prev) => prev.filter((x) => x.id !== id));
      toast.success("Campaign deleted");
      setConfirmDelete(null);
    } catch (e: any) {
      toast.error(e?.message || "Failed to delete campaign");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="px-6 md:px-10 py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Review Outreach</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Send review requests and follow up automatically
            </p>
          </div>
          <Button asChild>
            <Link to="/clientpanel/enagev1/outreach/new">
              <Plus className="h-4 w-4 mr-2" /> New Campaign
            </Link>
          </Button>
        </div>

        <div className="mt-8 rounded-lg border bg-card overflow-hidden">
          {loading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-5 flex-1" />
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <MailPlus className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">No campaigns yet</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                Reach out to your customers and start collecting reviews automatically.
              </p>
              <Button asChild className="mt-5">
                <Link to="/clientpanel/enagev1/outreach/new">
                  <Plus className="h-4 w-4 mr-2" /> Send your first review request
                </Link>
              </Button>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign name</TableHead>
                    <TableHead>Outlet</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead className="text-right">Recipients</TableHead>
                    <TableHead className="text-right">Delivered</TableHead>
                    <TableHead className="text-right">Opened</TableHead>
                    <TableHead className="text-right">Clicked</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>{r.outlet_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{r.channel === "sms" ? "SMS" : "Email"}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{r.recipients.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{r.delivered.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatPct(r.openedPct)}</TableCell>
                      <TableCell className="text-right">{formatPct(r.clickedPct)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusClass(r.status)}>
                          {titleCase(r.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/clientpanel/enagev1/outreach/${r.id}`}>View</Link>
                        </Button>
                        {r.status === "draft" && (
                          <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSend(r)}
                                disabled={sendingId === r.id}
                              >
                                {sendingId === r.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                                Send
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmDelete(r)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.name}" and all of its recipients will be permanently removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={!!deletingId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingId ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Outreach;
